import React from "react";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface InvestmentFormProps {
  formValues: Record<string, any>;
  onFieldChange: (fieldId: string, value: any) => void;
  className?: string;
  participants?: Array<{ id: string; vorname: string; nachname: string }>;
}

// Anlageformen
const INVESTMENT_TYPES = [
  { id: "aktien", label: "Aktien" },
  { id: "anleihen", label: "Anleihen" },
  { id: "fonds", label: "Fonds" },
  { id: "edelmetalle", label: "Edelmetalle" },
  { id: "bausparen", label: "Bausparen" },
  { id: "konto", label: "Konto" },
  { id: "sparbuch", label: "Sparbuch" },
  { id: "krypto", label: "Krypto" },
  { id: "sonstiges", label: "Sonstiges:" },
];

// Transaction amounts
const TRANSACTION_AMOUNTS = [
  { id: "unter_15000", label: "< € 15.000,-" },
  { id: "ueber_15000", label: "> € 15.000,-" },
  { id: "keine_trans", label: "keine" },
];

// Transaction counts
const TRANSACTION_COUNTS = [
  { id: "unter_10", label: "< 10" },
  { id: "ueber_10", label: "> 10" },
  { id: "keine_count", label: "keine" },
];

// Sustainability options
const SUSTAINABILITY_OPTIONS = [
  { id: "ja", label: "ja" },
  { id: "nein", label: "nein" },
  { id: "neutral", label: "neutral" },
];

export function InvestmentForm({
  formValues,
  onFieldChange,
  className,
  participants = [],
}: InvestmentFormProps) {
  const displayParticipants = participants.length > 0 
    ? participants 
    : [
        { id: "gp1", vorname: "Gesprächspartner", nachname: "1" },
        { id: "gp2", vorname: "Gesprächspartner", nachname: "2" },
      ];

  const investments: Record<string, Record<string, { checked: boolean; gesellschaft: string }>> = 
    formValues.investment_types || {};
  const transactionAmount: string = formValues.investment_transaction_amount || "";
  const transactionCount: string = formValues.investment_transaction_count || "";
  const sustainability: string = formValues.investment_sustainability || "";
  const noExisting: boolean = formValues.investment_no_existing || false;
  const copyPhoto: boolean = formValues.investment_copy_photo || false;
  const notes: string = formValues.investment_notes || "";

  const updateInvestment = (
    typeId: string,
    participantId: string,
    field: 'checked' | 'gesellschaft',
    value: boolean | string
  ) => {
    const typeData = investments[typeId] || {};
    const participantData = typeData[participantId] || { checked: false, gesellschaft: "" };
    const updated = {
      ...investments,
      [typeId]: {
        ...typeData,
        [participantId]: {
          ...participantData,
          [field]: value,
        },
      },
    };
    onFieldChange("investment_types", updated);
  };

  const getParticipantLabel = (participant: typeof displayParticipants[0], index: number) => {
    const name = `${participant.vorname || ''} ${participant.nachname || ''}`.trim();
    return name || `GP${index + 1}`;
  };

  return (
    <div className={cn("p-6 space-y-8", className)}>
      {/* Title */}
      <div className="text-left">
        <h2 className="text-2xl md:text-3xl font-bold">
          <span className="text-foreground">Geld </span>
          <span className="text-primary">investieren</span>
        </h2>
        <p className="text-lg italic text-muted-foreground mt-1">
          Heute investieren, morgen profitieren.
        </p>
      </div>

      {/* Investment Types Table */}
      <div className="space-y-4">
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-primary text-primary-foreground">
                <th className="text-left p-3 font-semibold">Anlageform</th>
                {displayParticipants.map((participant, pIdx) => (
                  <React.Fragment key={participant.id}>
                    <th className="text-center p-3 font-semibold whitespace-nowrap">
                      {getParticipantLabel(participant, pIdx).slice(0, 4)}
                    </th>
                    <th className="text-center p-3 font-semibold">Gesellschaft</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {INVESTMENT_TYPES.map((type) => (
                <tr key={type.id} className="border-t">
                  <td className="p-3 font-medium text-sm">{type.label}</td>
                  {displayParticipants.map((participant) => (
                    <React.Fragment key={participant.id}>
                      <td className="p-2 text-center">
                        <Checkbox
                          checked={investments[type.id]?.[participant.id]?.checked || false}
                          onCheckedChange={(checked) =>
                            updateInvestment(type.id, participant.id, 'checked', !!checked)
                          }
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={investments[type.id]?.[participant.id]?.gesellschaft || ""}
                          onChange={(e) =>
                            updateInvestment(type.id, participant.id, 'gesellschaft', e.target.value)
                          }
                          className="h-8 text-sm"
                          placeholder=""
                        />
                      </td>
                    </React.Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Checkboxes below table */}
        <div className="flex gap-6">
          <div className="flex items-center gap-2">
            <Checkbox
              id="inv-no-existing"
              checked={noExisting}
              onCheckedChange={(checked) => onFieldChange("investment_no_existing", checked)}
            />
            <Label htmlFor="inv-no-existing" className="text-sm cursor-pointer">
              keine vorhanden
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="inv-copy-photo"
              checked={copyPhoto}
              onCheckedChange={(checked) => onFieldChange("investment_copy_photo", checked)}
            />
            <Label htmlFor="inv-copy-photo" className="text-sm cursor-pointer">
              Kopie oder Foto
            </Label>
          </div>
        </div>
      </div>

      {/* Transaction Amount Question */}
      <div className="space-y-3">
        <h3 className="font-semibold text-foreground">
          Höhe der Wertpapiertransaktionen in den letzten 12 Monaten
        </h3>
        <div className="flex flex-wrap gap-6">
          {TRANSACTION_AMOUNTS.map((option) => (
            <div key={option.id} className="flex items-center gap-2">
              <Checkbox
                id={`trans-amount-${option.id}`}
                checked={transactionAmount === option.id}
                onCheckedChange={() =>
                  onFieldChange("investment_transaction_amount", transactionAmount === option.id ? "" : option.id)
                }
              />
              <Label htmlFor={`trans-amount-${option.id}`} className="text-sm cursor-pointer">
                {option.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Transaction Count Question */}
      <div className="space-y-3">
        <h3 className="font-semibold text-foreground">
          Anzahl der Wertpapiertransaktionen in den letzten 12 Monaten
        </h3>
        <div className="flex flex-wrap gap-6">
          {TRANSACTION_COUNTS.map((option) => (
            <div key={option.id} className="flex items-center gap-2">
              <Checkbox
                id={`trans-count-${option.id}`}
                checked={transactionCount === option.id}
                onCheckedChange={() =>
                  onFieldChange("investment_transaction_count", transactionCount === option.id ? "" : option.id)
                }
              />
              <Label htmlFor={`trans-count-${option.id}`} className="text-sm cursor-pointer">
                {option.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Sustainability Question */}
      <div className="space-y-3">
        <h3 className="font-semibold text-foreground">
          Möchten Sie Ihr Investment nachhaltig ausrichten?
        </h3>
        <div className="flex flex-wrap gap-6">
          {SUSTAINABILITY_OPTIONS.map((option) => (
            <div key={option.id} className="flex items-center gap-2">
              <Checkbox
                id={`sustain-${option.id}`}
                checked={sustainability === option.id}
                onCheckedChange={() =>
                  onFieldChange("investment_sustainability", sustainability === option.id ? "" : option.id)
                }
              />
              <Label htmlFor={`sustain-${option.id}`} className="text-sm cursor-pointer">
                {option.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Notes Section */}
      <div className="space-y-3">
        <div className="border-2 border-muted rounded-lg p-4">
          <Label className="text-sm font-semibold text-foreground">Notizen:</Label>
          <Textarea
            value={notes}
            onChange={(e) => onFieldChange("investment_notes", e.target.value)}
            placeholder="Ihre Notizen hier..."
            className="mt-2 min-h-[100px] resize-none"
          />
        </div>
      </div>
    </div>
  );
}
